/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    deleteRoute,
    fetchAllRoutes,
    fetchRoute,
    resetRoute
} from "./features/inspectionRouteSlice";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import FeatherIcon from "feather-icons-react";
import Button from "components/base/Button";
import dayjs from "dayjs";
import { Periodicity } from "../model/enumerations/periodicity.model";

import { InspectionRouteFormCreate } from "./InspectionRouteFormCreate";
import PhoenixModal from "../components/PhoenixModal";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { getDateLocaleConfig } from "i18n/locale";

export default function InspectionRoutes() {
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const dateLocale = getDateLocaleConfig(i18n.language);
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale.intlLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }),
        [dateLocale.intlLocale]
    );
    const dateTimeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale.intlLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        [dateLocale.intlLocale]
    );
    const dispatch = useDispatch();

    const routesList = useSelector((state: any) => state.inspectionRoute?.allRoutes ?? []);
    const loading = useSelector((state: any) =>
        Boolean(state.inspectionRoute?.loading ?? state.inspectionRoute?.isPending)
    );

    const [openModal, setOpenModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [routeToDelete, setRouteToDelete] = useState<any>(null);

    const [globalFilter, setGlobalFilter] = useState<string>("");

    useEffect(() => {
        dispatch(fetchAllRoutes());
    }, [dispatch]);

    const handleConfirmDelete = (route: any) => {
        setRouteToDelete(route);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!routeToDelete) return;

        const result = await dispatch(deleteRoute(routeToDelete.id));
        if (deleteRoute.fulfilled.match(result)) {
            setShowDeleteModal(false);
            setRouteToDelete(null);
        }
    };

    // (opcional) para permitir filtrar "Periodicidade" pelo texto no globalFilter,
    // criamos um campo auxiliar.
    const tableData = useMemo(() => {
        const periodicityMap: Record<string, string> = {
            MONTHLY: tt("pages.thermography.inspectionRoutes.periodicity.monthly"),
            QUARTERLY: tt("pages.thermography.inspectionRoutes.periodicity.quarterly"),
            SEMI_ANNUAL: tt("pages.thermography.inspectionRoutes.periodicity.semiAnnual"),
            ANNUAL: tt("pages.thermography.inspectionRoutes.periodicity.annual"),
            APERIODIC: tt("pages.thermography.inspectionRoutes.periodicity.aperiodic")
        };
        return (routesList ?? []).map((r: any) => ({
            ...r,
            periodicityLabel: periodicityMap[r.periodicity] ?? Periodicity[r.periodicity] ?? ""
        }));
    }, [routesList, t]);

    const periodicityTemplate = (row: any) => row.periodicityLabel ?? "";
    const plantTemplate = (row: any) => row.plant?.name ?? "";

    const dateTemplate = (row: any, field: string, withTime = false) => {
        const raw = row?.[field] as string | null | undefined;
        if (!raw) return "";
        const parsed = dayjs(raw);
        if (!parsed.isValid()) return "";
        return (withTime ? dateTimeFormatter : dateFormatter).format(parsed.toDate());
    };

    const actionBodyTemplate = (row: any) => (
        <div className="d-flex justify-content-end align-items-center gap-3">
            {/* Editar */}
            <span
                role="button"
                onClick={(e) => {
                    e.stopPropagation();
                    dispatch(fetchRoute(row.id));
                    setOpenModal(true);
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.15)";
                    e.currentTarget.style.color = "#6299ffff";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.color = "#6f6f6f";
                }}
                className="cursor-pointer"
                style={{
                    fontSize: 18,
                    color: "#6f6f6f",
                    transition: "all 0.15s ease-in-out"
                }}
                title={tt("pages.thermography.inspectionRoutes.actions.editTitle")}
            >
                <FeatherIcon icon="edit-2" size={16} />
            </span>

            {/* Excluir */}
            <span
                role="button"
                onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmDelete(row);
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.15)";
                    e.currentTarget.style.color = "#ff4d4f";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.color = "#dc3545";
                }}
                className="cursor-pointer"
                style={{
                    fontSize: 18,
                    color: "#dc3545",
                    transition: "all 0.15s ease-in-out"
                }}
                title={tt("pages.thermography.inspectionRoutes.actions.removeTitle")}
            >
                <FeatherIcon icon="trash-2" size={16} />
            </span>
        </div>
    );

    const tableHeader = (
        <div className="d-flex justify-content-end">
            <span className="p-input-icon-left">
                <InputText
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={tt("pages.thermography.inspectionRoutes.searchPlaceholder")}
                    className="p-inputtext-sm"
                />
            </span>
        </div>
    );

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{tt("pages.thermography.inspectionRoutes.title")}</h4>

                <Button
                    variant="primary"
                    onClick={() => {
                        dispatch(resetRoute());
                        setOpenModal(true);
                    }}
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                >
                    {tt("pages.thermography.inspectionRoutes.newRoute")}
                </Button>
            </div>

            <DataTable
                key={`inspection-routes-table-${i18n.language}`}
                value={tableData}
                loading={loading}
                className="custom-datatable"
                size="small"
                dataKey="id"
                removableSort
                scrollable
                scrollHeight="600px"
                globalFilter={globalFilter}
                globalFilterFields={[
                    "name",
                    "maintenancePlan",
                    "periodicityLabel",
                    "duration",
                    "plant.name"
                ]}
                emptyMessage={tt("pages.thermography.inspectionRoutes.emptyMessage")}
                header={tableHeader}
            >
                <Column sortable field="name" header={tt("pages.thermography.inspectionRoutes.table.name")} />
                <Column sortable field="maintenancePlan" header={tt("pages.thermography.inspectionRoutes.table.plan")} />
                <Column
                    sortable
                    field="periodicity"
                    header={tt("pages.thermography.inspectionRoutes.table.periodicity")}
                    body={periodicityTemplate}
                />
                <Column sortable field="duration" header={tt("pages.thermography.inspectionRoutes.table.days")} />
                <Column sortable field="plant.name" header={tt("pages.thermography.inspectionRoutes.table.installation")} body={plantTemplate} />

                <Column
                    sortable
                    field="expectedStartDate"
                    header={tt("pages.thermography.inspectionRoutes.table.expectedStart")}
                    body={(row) => dateTemplate(row, "expectedStartDate")}
                />

                <Column
                    sortable
                    field="createdAt"
                    header={tt("pages.thermography.inspectionRoutes.table.createdAt")}
                    body={(row) => dateTemplate(row, "createdAt", true)}
                />

                <Column
                    header={tt("pages.thermography.inspectionRoutes.table.actions")}
                    body={actionBodyTemplate}
                    style={{ width: "120px" }}
                />
            </DataTable>

            <InspectionRouteFormCreate visible={openModal} onHide={() => setOpenModal(false)} />

            <PhoenixModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                title={tt("pages.thermography.inspectionRoutes.deleteModal.title")}
                size="fullscreen-sm-down"
                backdrop="static"
                footer={
                    <>
                        <Button variant="phoenix-secondary" onClick={() => setShowDeleteModal(false)}>
                            {tt("pages.thermography.inspectionRoutes.deleteModal.cancel")}
                        </Button>

                        <Button variant="primary" onClick={handleDelete}>
                            {tt("pages.thermography.inspectionRoutes.deleteModal.confirm")}
                        </Button>
                    </>
                }
            >
                {tt("pages.thermography.inspectionRoutes.deleteModal.messagePrefix")}
                <strong> {routeToDelete?.name ?? routeToDelete?.code} </strong>?
            </PhoenixModal>
        </div>
    );
}
